function preferredLanguage(languages, supported, fallback) {
  return languages.map(value => String(value).toLowerCase().split('-')[0]).find(value => supported.includes(value)) || fallback;
}

function chooseDestination(country, languages = ['en']) {
  const fixed = {
    CH: ['https://www.rubin-extensions.ch/', 'de'],
    LI: ['https://www.rubin-extensions.ch/', 'de'],
    DE: ['https://www.rubin-extensions.de/', 'de'],
    AT: ['https://www.rubin-extensions.at/', 'de'],
    PL: ['https://www.rubin-extensions.pl/', 'pl'],
    GB: ['https://rubin-extensions.co.uk/', 'en'],
    AU: ['https://rubin-extensions.com.au/', 'en'],
    US: ['https://rubin-extensions.com/', 'en']
  };
  if (fixed[country]) return { url: fixed[country][0], language: fixed[country][1], region: country };
  const regional = { FR: [['fr', 'en'], 'fr'], IT: [['it', 'en'], 'it'], ES: [['es', 'en'], 'es'], CA: [['en', 'fr'], 'en'], AE: [['ar', 'en'], 'en'], CN: [['zh', 'en'], 'zh'] };
  if (regional[country]) {
    const language = preferredLanguage(languages, regional[country][0], regional[country][1]);
    return { url: 'https://rubin-extensions.com/' + language + '-' + country.toLowerCase(), language, region: country };
  }
  return { url: 'https://rubin-extensions.com/', language: 'en', region: 'INT' };
}

function shouldSuggest(destination, currentUrl) {
  const target = new URL(destination.url);
  const current = new URL(currentUrl);
  const normalizeHost = host => host.toLowerCase().replace(/^www\./, '');
  if (normalizeHost(target.hostname) !== normalizeHost(current.hostname)) return true;
  const targetPath = target.pathname.replace(/\/$/, '');
  const currentPath = current.pathname.toLowerCase();
  if (targetPath) return currentPath !== targetPath && !currentPath.startsWith(targetPath + '/');
  return /^\/[a-z]{2}(?:-[a-z]{2})?(?:\/|$)/.test(currentPath);
}

function initializeRegionDialog() {
  const configElement = document.getElementById('rx-region-config');
  const dialog = document.getElementById('rx-region-dialog');
  if (!configElement || !dialog || typeof dialog.showModal !== 'function') return;
  let config;
  let messages;
  try {
    config = JSON.parse(configElement.textContent);
    const rawMessages = typeof config.messages === 'string' ? JSON.parse(config.messages) : config.messages;
    messages = Object.fromEntries(Object.entries(rawMessages).map(([language, copy]) => [language, typeof copy === 'string' ? JSON.parse(copy) : copy]));
  } catch { return; }
  const storageKey = 'rubin_region_choice_v1';
  const lifetime = 30 * 24 * 60 * 60 * 1000;
  const readChoice = () => {
    for (const storage of ['localStorage', 'sessionStorage']) {
      try {
        const saved = JSON.parse(window[storage].getItem(storageKey) || 'null');
        if (saved && saved.expires > Date.now()) return true;
      } catch {}
    }
    return false;
  };
  const remember = choice => {
    const value = JSON.stringify({ choice, expires: Date.now() + lifetime });
    for (const storage of ['localStorage', 'sessionStorage']) {
      try { window[storage].setItem(storageKey, value); return; } catch {}
    }
  };
  const current = new URL(window.location.href);
  if (current.searchParams.get('rx_store_choice') === '1') {
    remember('yes');
    current.searchParams.delete('rx_store_choice');
    window.history.replaceState(window.history.state, '', current.href);
    return;
  }
  if (readChoice() || config.cartCount > 0) return;
  const languages = navigator.languages && navigator.languages.length ? [...navigator.languages] : [navigator.language || 'en'];
  const language = preferredLanguage(languages, Object.keys(messages), 'en');
  const copy = messages[language] || messages.en;
  if (!copy) return;
  // Rubin 2026-10-28: erkanntes Land kurz pro Sitzung merken (60 Min., sessionStorage),
  // damit passende Besucher (z. B. aus CH/LI) nicht bei jedem Seitenaufruf neu abgefragt werden.
  const countryKey = 'rubin_region_country_v1';
  const countryLifetime = 60 * 60 * 1000;
  const readCountry = () => {
    try {
      const saved = JSON.parse(window.sessionStorage.getItem(countryKey) || 'null');
      if (saved && saved.expires > Date.now() && /^[A-Z]{2}$/.test(saved.country || '')) return saved.country;
    } catch {}
    return null;
  };
  const rememberCountry = value => {
    try { window.sessionStorage.setItem(countryKey, JSON.stringify({ country: value, expires: Date.now() + countryLifetime })); } catch {}
  };
  const run = async () => {
    if (readChoice()) return;
    let country = readCountry();
    if (!country) {
      const controller = new AbortController();
      const timeout = window.setTimeout(() => controller.abort(), 4500);
      try {
        const root = window.Shopify?.routes?.root || '/';
        const endpoint = new URL(root + 'browsing_context_suggestions.json', window.location.origin);
        endpoint.searchParams.set('country[enabled]', 'true');
        endpoint.searchParams.set('language[enabled]', 'true');
        const response = await fetch(endpoint.href, { credentials: 'same-origin', signal: controller.signal });
        if (!response.ok) return;
        const data = await response.json();
        country = data.detected_values?.country?.handle;
      } catch { return; }
      finally { window.clearTimeout(timeout); }
      if (/^[A-Z]{2}$/.test(country || '')) rememberCountry(country);
    }
    if (!/^[A-Z]{2}$/.test(country || '')) return;
    const destination = chooseDestination(country, languages);
    if (!shouldSuggest(destination, window.location.href) || readChoice()) return;
    let countryName = country;
    let destinationName = destination.region === 'INT' ? copy.international : destination.region;
    let languageName = destination.language;
    try {
      const regions = new Intl.DisplayNames([language], { type: 'region' });
      countryName = regions.of(country) || country;
      if (destination.region !== 'INT') destinationName = regions.of(destination.region) || destination.region;
      languageName = new Intl.DisplayNames([language], { type: 'language' }).of(destination.language) || destination.language;
    } catch {}
    const label = destinationName + ' · ' + languageName;
    dialog.lang = language;
    dialog.dir = language === 'ar' ? 'rtl' : 'ltr';
    dialog.querySelector('[data-rx-region-title]').textContent = copy.title;
    dialog.querySelector('[data-rx-region-description]').textContent = copy.body.replace('{country}', countryName).replace('{destination}', label);
    const yes = dialog.querySelector('[data-rx-region-yes]');
    const no = dialog.querySelector('[data-rx-region-no]');
    yes.textContent = copy.yes;
    no.textContent = copy.no;
    let decided = false;
    no.addEventListener('click', () => { decided = true; remember('no'); dialog.close(); });
    yes.addEventListener('click', () => {
      decided = true;
      remember('yes');
      const next = new URL(destination.url);
      next.searchParams.set('rx_store_choice', '1');
      dialog.close();
      window.location.assign(next.href);
    });
    dialog.addEventListener('close', () => { if (!decided) remember('no'); });
    if (document.querySelector('dialog[open]')) return;
    dialog.showModal();
  };
  const schedule = () => {
    if ('requestIdleCallback' in window) window.requestIdleCallback(run, { timeout: 3000 });
    else window.setTimeout(run, 300);
  };
  if (document.readyState === 'complete') schedule();
  else window.addEventListener('load', schedule, { once: true });
}

if (typeof window !== 'undefined') initializeRegionDialog();
export { chooseDestination, shouldSuggest };
